
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface Board {
  id: string;
  name: string;
}

interface Workspace {
  id: string;
  name: string;
  organization_id: string;
  boards: Board[];
}

interface Organization {
  id: string;
  name: string;
  workspaces: Workspace[];
}

export const useOrganizationManagement = () => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOrganizations = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.rpc('get_organizations_with_workspaces');
      if (error) throw error;
      
      const orgsArray = Array.isArray(data) 
        ? data.map(org => ({
            id: org.id,
            name: org.name,
            workspaces: (org.workspaces as any[] || []).map(ws => ({
              id: ws.id,
              name: ws.name,
              organization_id: ws.organization_id,
              boards: []  // We'll implement board fetching later if needed
            }))
          }))
        : [];

      setOrganizations(orgsArray);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error(t('Error fetching data'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRename = async (
    type: 'organization' | 'workspace' | 'board',
    id: string,
    newName: string
  ) => {
    if (!newName.trim()) return;

    try {
      let error;
      switch (type) {
        case 'organization':
          ({ error } = await supabase
            .from('organizations')
            .update({ name: newName })
            .eq('id', id));
          break;
        case 'workspace':
          ({ error } = await supabase
            .from('workspaces')
            .update({ name: newName })
            .eq('id', id));
          break;
        case 'board':
          ({ error } = await supabase
            .from('boards')
            .update({ name: newName })
            .eq('id', id));
          break;
      }

      if (error) throw error;

      toast.success(t('Renamed successfully'));
      fetchOrganizations();
    } catch (error) {
      console.error('Error renaming:', error);
      toast.error(t('Error renaming'));
    }
  };

  const handleDelete = async (
    type: 'organization' | 'workspace' | 'board',
    id: string
  ) => {
    try {
      let error;
      switch (type) {
        case 'organization':
          ({ error } = await supabase
            .from('organizations')
            .delete()
            .eq('id', id));
          break;
        case 'workspace':
          ({ error } = await supabase
            .from('workspaces')
            .update({ archived_at: new Date().toISOString() })
            .eq('id', id));
          break;
        case 'board':
          ({ error } = await supabase
            .from('boards')
            .delete()
            .eq('id', id));
          break;
      }

      if (error) throw error;

      toast.success(t('Deleted successfully'));
      fetchOrganizations();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(t('Error deleting'));
    }
  };

  return {
    organizations,
    isLoading,
    fetchOrganizations,
    handleRename,
    handleDelete,
  };
};
